import type { CmiData } from "@/components/lms/lmsScormRuntime";

const SCORM_LAUNCH_CONFIG_PREFIX = "kaer:scorm-launch:";
const SCORM_ENROLMENT_RESERVATION_PREFIX = "kaer:scorm-enrolment:";
const SCORM_LAUNCH_CONFIG_TTL_MS = 24 * 60 * 60 * 1000;
const SCORM_LAUNCH_HANDOFF_TTL_MS = 2 * 60 * 1000;
const SCORM_ACTIVE_RESERVATION_TTL_MS = 30 * 1000;
const SCORM_RESERVATION_REFRESH_MS = 10 * 1000;

interface ScormLaunchMessages {
  completionNotice: string;
  completionSaving: string;
  continueLabel: string;
  duplicateSession: string;
  launchError: string;
  saveWarning: string;
}

interface StoredScormLaunchConfig {
  schemaVersion: 2;
  launchId: string;
  enrolmentId: string;
  courseId: string;
  cmiData: CmiData;
  completionUrl: string;
  runtimeEndpoint: string;
  messages: ScormLaunchMessages;
  createdAt: number;
  lastUpdatedAt: number;
}

interface ScormLaunchReservation {
  launchId: string;
  expiresAt: number;
}

const getScormLaunchStorageKey = (launchId: string) =>
  `${SCORM_LAUNCH_CONFIG_PREFIX}${launchId}`;

const getScormReservationStorageKey = (enrolmentId: string) =>
  `${SCORM_ENROLMENT_RESERVATION_PREFIX}${enrolmentId}`;

const parseStoredValue = <T>(value: string | null): T | null => {
  if (!value) return null;

  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

const readStoredScormLaunchConfig = (
  launchId: string,
  storage: Storage = window.localStorage,
) =>
  parseStoredValue<StoredScormLaunchConfig>(
    storage.getItem(getScormLaunchStorageKey(launchId)),
  );

const writeStoredScormLaunchConfig = (
  config: StoredScormLaunchConfig,
  storage: Storage = window.localStorage,
) => {
  storage.setItem(getScormLaunchStorageKey(config.launchId), JSON.stringify(config));
};

const removeStoredScormLaunchConfig = (
  launchId: string,
  storage: Storage = window.localStorage,
) => {
  storage.removeItem(getScormLaunchStorageKey(launchId));
};

const updateStoredScormCmiData = (
  config: StoredScormLaunchConfig,
  cmiData: CmiData,
  storage: Storage = window.localStorage,
) => {
  const updatedConfig = {
    ...config,
    cmiData: { ...cmiData },
    lastUpdatedAt: Date.now(),
  };
  writeStoredScormLaunchConfig(updatedConfig, storage);
  return updatedConfig;
};

const readScormLaunchReservation = (
  enrolmentId: string,
  storage: Storage = window.localStorage,
) =>
  parseStoredValue<ScormLaunchReservation>(
    storage.getItem(getScormReservationStorageKey(enrolmentId)),
  );

const reserveScormLaunch = ({
  enrolmentId,
  launchId,
  ttlMs = SCORM_LAUNCH_HANDOFF_TTL_MS,
  replaceExisting = false,
  storage = window.localStorage,
  now = Date.now(),
}: {
  enrolmentId: string;
  launchId: string;
  ttlMs?: number;
  replaceExisting?: boolean;
  storage?: Storage;
  now?: number;
}) => {
  const currentReservation = readScormLaunchReservation(enrolmentId, storage);
  if (
    currentReservation &&
    currentReservation.launchId !== launchId &&
    currentReservation.expiresAt > now &&
    !replaceExisting
  ) {
    return false;
  }

  storage.setItem(
    getScormReservationStorageKey(enrolmentId),
    JSON.stringify({ launchId, expiresAt: now + ttlMs }),
  );
  return true;
};

const refreshScormLaunchReservation = (
  enrolmentId: string,
  launchId: string,
  storage: Storage = window.localStorage,
) =>
  reserveScormLaunch({
    enrolmentId,
    launchId,
    storage,
    ttlMs: SCORM_ACTIVE_RESERVATION_TTL_MS,
  });

const releaseScormLaunchReservation = (
  enrolmentId: string,
  launchId: string,
  storage: Storage = window.localStorage,
) => {
  const reservation = readScormLaunchReservation(enrolmentId, storage);
  if (reservation?.launchId === launchId) {
    storage.removeItem(getScormReservationStorageKey(enrolmentId));
  }
};

const cleanupExpiredScormLaunchData = (
  storage: Storage = window.localStorage,
  now = Date.now(),
) => {
  for (let index = storage.length - 1; index >= 0; index -= 1) {
    const key = storage.key(index);
    if (!key) continue;

    if (key.startsWith(SCORM_LAUNCH_CONFIG_PREFIX)) {
      const config = parseStoredValue<StoredScormLaunchConfig>(storage.getItem(key));
      if (!config || now - config.lastUpdatedAt > SCORM_LAUNCH_CONFIG_TTL_MS) {
        storage.removeItem(key);
      }
    }

    if (key.startsWith(SCORM_ENROLMENT_RESERVATION_PREFIX)) {
      const reservation = parseStoredValue<ScormLaunchReservation>(
        storage.getItem(key),
      );
      if (!reservation || reservation.expiresAt <= now) {
        storage.removeItem(key);
      }
    }
  }
};

const getScormLaunchLockName = (enrolmentId: string) =>
  `kaer-scorm-enrolment:${enrolmentId}`;

const runWithScormLaunchLock = async <T>({
  enrolmentId,
  launchId,
  onAcquired,
  storage = window.localStorage,
}: {
  enrolmentId: string;
  launchId: string;
  onAcquired: () => Promise<T>;
  storage?: Storage;
}): Promise<{ acquired: boolean; result?: T }> => {
  const run = async (replaceExisting: boolean) => {
    const reserved = reserveScormLaunch({
      enrolmentId,
      launchId,
      replaceExisting,
      storage,
    });
    if (!reserved) return { acquired: false };

    try {
      return { acquired: true, result: await onAcquired() };
    } catch (error) {
      releaseScormLaunchReservation(enrolmentId, launchId, storage);
      throw error;
    }
  };

  if (!navigator.locks) {
    return run(false);
  }

  return navigator.locks.request(
    getScormLaunchLockName(enrolmentId),
    { ifAvailable: true, mode: "exclusive" },
    async (lock) => (lock ? run(true) : { acquired: false }),
  );
};

export {
  cleanupExpiredScormLaunchData,
  getScormLaunchLockName,
  getScormLaunchStorageKey,
  readStoredScormLaunchConfig,
  removeStoredScormLaunchConfig,
  refreshScormLaunchReservation,
  releaseScormLaunchReservation,
  reserveScormLaunch,
  runWithScormLaunchLock,
  SCORM_ACTIVE_RESERVATION_TTL_MS,
  SCORM_RESERVATION_REFRESH_MS,
  updateStoredScormCmiData,
  writeStoredScormLaunchConfig,
};

export type { ScormLaunchMessages, StoredScormLaunchConfig };
